import { ListMonitoringEntriesQueryControllerListCheckSlug } from "@/generated/model/listMonitoringEntriesQueryControllerListCheckSlug";
import { ListMonitoringEntriesQueryControllerListScope } from "@/generated/model/listMonitoringEntriesQueryControllerListScope";
import type { ListMonitoringEntriesQueryControllerListSortBy } from "@/generated/model/listMonitoringEntriesQueryControllerListSortBy";
import type { MonitoringEntryDto } from "@/generated/model/monitoringEntryDto";
import { MonitoringEntryDtoSource } from "@/generated/model/monitoringEntryDtoSource";
import { MonitoringEntryDtoValueKind } from "@/generated/model/monitoringEntryDtoValueKind";

export type { SortDir } from "@/lib/category-display";

/**
 * The two Monitoring page tabs. Deliberately not derived from the API's scope
 * enum: History covers two scopes (readings and resolved episodes), so a tab is
 * no longer one-to-one with a scope — deriving it would let a scope value be
 * passed where a tab is expected and type-check.
 */
export type MonitoringTab = "alerts" | "history";

/** The History tab's sub-views. Readings is the default. */
export type MonitoringHistoryView = "readings" | "resolved-alerts";

/** The three lists the table can render, flattened from tab + sub-view. */
export type MonitoringView = "alerts" | "readings" | "resolved-alerts";

export function monitoringViewFor(
  tab: MonitoringTab,
  view: MonitoringHistoryView,
): MonitoringView {
  return tab === "alerts" ? "alerts" : view;
}

/** Single mapping from the page's URL state onto the API scope it reads. */
export function monitoringScopeFor(
  tab: MonitoringTab,
  view: MonitoringHistoryView,
): ListMonitoringEntriesQueryControllerListScope {
  if (tab === "alerts")
    return ListMonitoringEntriesQueryControllerListScope.alerts;
  return view === "resolved-alerts"
    ? ListMonitoringEntriesQueryControllerListScope["resolved-alerts"]
    : ListMonitoringEntriesQueryControllerListScope.history;
}

/** Sort columns supported server-side, derived from the API allow-list. */
export type MonitoringSortColumn =
  `${ListMonitoringEntriesQueryControllerListSortBy}`;

export const PAGE_SIZE = 10;

export const EMERGENCY_LIGHTING_LOG_TYPE_SLUG =
  ListMonitoringEntriesQueryControllerListCheckSlug["emergency-lighting"];
export const FIRE_EXTINGUISHER_CHECK_SLUG =
  ListMonitoringEntriesQueryControllerListCheckSlug["fire-extinguisher-check"];
/** The two emergency lighting checks (NAU-411). Composite checks, not LogTypes. */
export const EMERGENCY_LIGHT_FLICK_TEST_SLUG =
  ListMonitoringEntriesQueryControllerListCheckSlug[
    "emergency-light-flick-test"
  ];
export const EMERGENCY_LIGHT_VISUAL_CHECK_SLUG =
  ListMonitoringEntriesQueryControllerListCheckSlug[
    "emergency-light-visual-check"
  ];
export const EMERGENCY_LIGHT_CHECK_SLUGS: readonly string[] = [
  EMERGENCY_LIGHT_FLICK_TEST_SLUG,
  EMERGENCY_LIGHT_VISUAL_CHECK_SLUG,
];
/** Seed-data slugs: the API has no enum for these, so each lives only here. */
export const FRIDGE_TEMPERATURE_LOG_TYPE_SLUG = "fridge-temperature";
export const GARDEN_SUPERVISION_LOG_TYPE_SLUG = "garden-supervision";
export const SLEEP_CHECK_LOG_TYPE_SLUG = "sleep-check";
/** Event type, not a log type — re-exported by the fire-drill component. */
export const FIRE_DRILL_SLUG =
  ListMonitoringEntriesQueryControllerListCheckSlug["fire-drill"];

const FOOD_HYGIENE_CATEGORY_SLUG = "food-hygiene-safety-and-nutrition";
const FIRE_SAFETY_CATEGORY_SLUG = "fire-safety";
const PREMISES_CATEGORY_SLUG = "premises-and-environment";
const SAFEGUARDING_CATEGORY_SLUG = "safeguarding-children";

/**
 * Monitoring log types don't carry a category of their own, so their icon and
 * colour follow the closest category in the cross-cutting icon matrix: the
 * temperature monitors are food safety, the lighting, extinguisher and fire
 * drill records are fire safety, garden welfare is premises and sleep checks
 * are safeguarding. Unknown slugs keep the original food-hygiene default.
 */
export function monitoringCategorySlug(logTypeSlug: string): string {
  switch (logTypeSlug) {
    case EMERGENCY_LIGHTING_LOG_TYPE_SLUG:
    case EMERGENCY_LIGHT_FLICK_TEST_SLUG:
    case EMERGENCY_LIGHT_VISUAL_CHECK_SLUG:
    case FIRE_EXTINGUISHER_CHECK_SLUG:
    case FIRE_DRILL_SLUG:
      return FIRE_SAFETY_CATEGORY_SLUG;
    case GARDEN_SUPERVISION_LOG_TYPE_SLUG:
      return PREMISES_CATEGORY_SLUG;
    case SLEEP_CHECK_LOG_TYPE_SLUG:
      return SAFEGUARDING_CATEGORY_SLUG;
    default:
      return FOOD_HYGIENE_CATEGORY_SLUG;
  }
}

/** True when the row reads as a pass/fail check rather than a measurement. */
export function isPassFail(entry: MonitoringEntryDto): boolean {
  return entry.valueKind === MonitoringEntryDtoValueKind.PASS_FAIL;
}

/**
 * True when the row recorded a check with no target to judge it against, so it
 * has no verdict either — a garden welfare check, a sleep check, or a food
 * temperature (which measures but snapshots no threshold).
 */
export function isUntargeted(entry: MonitoringEntryDto): boolean {
  return entry.valueKind === MonitoringEntryDtoValueKind.UNTARGETED;
}

/**
 * Range sub-label under the alert name. Temperature rows read in/out of range;
 * pass/fail rows read the failure reason (e.g. "Pressure, Seals failed") so a
 * manager can see what went wrong without opening the record; untargeted rows
 * read neutrally, because "In range" would claim a range that doesn't exist.
 */
export function rangeLabel(entry: MonitoringEntryDto): {
  label: string;
  className: string;
} {
  if (isUntargeted(entry)) {
    return { label: "Recorded", className: "text-subtext-color" };
  }
  if (isPassFail(entry)) {
    return entry.isOutOfRange
      ? {
          label: entry.failureDetail ?? "Failed",
          className: "text-error-600",
        }
      : { label: "Passed", className: "text-success-600" };
  }
  return entry.isOutOfRange
    ? { label: "Out of range", className: "text-error-600" }
    : { label: "In range", className: "text-success-600" };
}

/** Value chip formatting, e.g. "0°C" or "Fail", tinted by the range judgement. */
export function formatValue(entry: MonitoringEntryDto): string {
  if (isPassFail(entry)) {
    return entry.isOutOfRange ? "Fail" : "Pass";
  }
  if (entry.value === null || entry.value === undefined) return "—";
  return `${entry.value}${entry.unit}`;
}

/** Where a Monitoring row's View action (and row click) navigates to. */
export type MonitoringViewTarget =
  | { kind: "incident"; incidentId: string }
  | { kind: "hot-holding-alert"; alertId: string }
  | { kind: "extinguisher-check"; checkId: string }
  | { kind: "emergency-light-check"; checkId: string }
  | { kind: "event-record"; eventRecordId: string }
  | { kind: "legacy-emergency-lighting" }
  | { kind: "log-day"; logTypeId: string; coldStorageId?: string };

/**
 * Resolves a row's destination, or null when it has none. This is the single
 * source of truth for both the View affordance and the row click: gating the
 * button on the same result that drives navigation is what stops a row from
 * rendering a View that silently does nothing (NAU-363).
 */
export function monitoringViewTarget(
  entry: MonitoringEntryDto,
  logTypes: readonly { id: string; slug?: string | null }[],
): MonitoringViewTarget | null {
  if (entry.source === MonitoringEntryDtoSource.FRIDGE_INCIDENT) {
    return { kind: "incident", incidentId: entry.id };
  }
  if (entry.source === MonitoringEntryDtoSource.HOT_HOLDING_ALERT) {
    return { kind: "hot-holding-alert", alertId: entry.id };
  }
  // A fire drill is an event record, not a LogType, so it opens its own detail
  // page rather than a log day.
  if (entry.source === MonitoringEntryDtoSource.FIRE_DRILL) {
    return entry.sourceRefId
      ? { kind: "event-record", eventRecordId: entry.sourceRefId }
      : null;
  }
  // The extinguisher check isn't a LogType either, so it never resolves a
  // logTypeId — without its sourceRefId there is nothing to open.
  const isExtinguisher = entry.logTypeSlug === FIRE_EXTINGUISHER_CHECK_SLUG;
  if (isExtinguisher && !entry.sourceRefId) return null;
  if (isExtinguisher && entry.sourceRefId) {
    return { kind: "extinguisher-check", checkId: entry.sourceRefId };
  }

  // Same for the emergency lighting checks: composite checks with no LogType,
  // so the parent check id on the row is the only way to open them.
  const isEmergencyLight = EMERGENCY_LIGHT_CHECK_SLUGS.includes(
    entry.logTypeSlug ?? "",
  );
  if (isEmergencyLight) {
    return entry.sourceRefId
      ? { kind: "emergency-light-check", checkId: entry.sourceRefId }
      : null;
  }

  // The pre-NAU-411 room-level records. Their log type is inactive, so the
  // lookup below finds nothing; the flick test history shows them instead.
  if (entry.logTypeSlug === EMERGENCY_LIGHTING_LOG_TYPE_SLUG) {
    return { kind: "legacy-emergency-lighting" };
  }

  const logTypeId = logTypes.find(
    (logType) => logType.slug === entry.logTypeSlug,
  )?.id;
  if (!logTypeId) return null;
  if (entry.logTypeSlug !== FRIDGE_TEMPERATURE_LOG_TYPE_SLUG) {
    return { kind: "log-day", logTypeId };
  }
  return {
    kind: "log-day",
    logTypeId,
    coldStorageId: entry.locationId ?? undefined,
  };
}

/**
 * Who resolved an episode, matching the platform convention (name, else email).
 * A resolved episode always records the resolver's id, so a null `resolvedBy`
 * alongside a set `resolvedById` means the user was soft-deleted — named as
 * such rather than blanked, which would read as "nobody resolved this".
 */
export function resolvedByLabel(entry: MonitoringEntryDto): string {
  if (entry.resolvedBy) {
    return (
      [entry.resolvedBy.firstName, entry.resolvedBy.lastName]
        .filter(Boolean)
        .join(" ") || entry.resolvedBy.email
    );
  }
  return entry.resolvedById ? "Unknown user" : "—";
}

/**
 * Who performed the check, matching the platform convention (name, else email).
 * Every history row records its recorder's id, so a null `recordedBy` alongside
 * a set `recordedById` means the user was soft-deleted — named as such rather
 * than blanked, which would read as "nobody did this".
 */
export function recordedByLabel(entry: MonitoringEntryDto): string {
  if (entry.recordedBy) {
    return (
      [entry.recordedBy.firstName, entry.recordedBy.lastName]
        .filter(Boolean)
        .join(" ") || entry.recordedBy.email
    );
  }
  return entry.recordedById ? "Unknown user" : "—";
}

/** Target sub-text, e.g. "Target 1°C to 5°C" or "Target ≥ 65°C". */
export function targetLabel(entry: MonitoringEntryDto): string | null {
  const { targetMin, targetMax, unit } = entry;
  if (targetMin !== null && targetMax !== null) {
    return `Target ${targetMin}${unit} to ${targetMax}${unit}`;
  }
  if (targetMin !== null) return `Target ≥ ${targetMin}${unit}`;
  if (targetMax !== null) return `Target ≤ ${targetMax}${unit}`;
  return null;
}
